import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { Condition } from "@/types";

import type { ActivityPeriod } from "../ActionRecord";
import { type TimeWindow, currentMinute } from "../activity";

export interface UseInvocationActivityOptions {
  window: TimeWindow;
  /** Omit for the finest the window allows. */
  period?: ActivityPeriod;
  condition?: Condition;
  /** Milliseconds between refetches, or false. */
  refetchInterval?: number | false;
}

/**
 * Bucketed activity of the current org's invocations over a window.
 *
 * The query is keyed on the window's identity, not its bounds, which are resolved as each fetch
 * starts. A relative window refetched on an interval therefore keeps its data on screen while
 * the next fetch runs, the same as a refresh of anything else; a window that asks something
 * different is a new query, and `isPlaceholderData` says so while the previous one's data stands
 * in. The condition and period are part of the key for the same reason.
 */
export function useInvocationActivity({
  // Bound to a different name: `window` would shadow the DOM global for the whole body.
  window: timeWindow,
  period,
  condition,
  refetchInterval = false,
}: UseInvocationActivityOptions) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: [
      "invocationActivity",
      currentOrganization?.org_id,
      timeWindow.key,
      period ?? null,
      condition ?? null,
    ],
    queryFn: async ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }
      const { start, end } = timeWindow.resolve(currentMinute());
      return actionService.getInvocationActivity(
        currentOrganization.org_id,
        {
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          period,
          condition,
        },
        { abortSignal: signal },
      );
    },
    enabled: currentOrganization !== null,
    refetchInterval,
    // A window that ends at "now" has a live rightmost bucket, so the app's five-minute
    // default would leave the chart's leading edge stale on a remount or a focus with
    // nothing saying so. Same reason as `useInvocationBreakdown` and
    // `useInvocationQueueStats`.
    staleTime: 0,
    // Keep the old series on screen while a new window loads, so a zoom does not flash empty.
    placeholderData: keepPreviousData,
  });
}
